import React, {useEffect, useRef} from "react";
import "./CursorGlow.scss";
import "./CursorInteract.scss";
import useCursorInteract from "../../hooks/useCursorInteract";

export default function CursorGlow({enabled = true}) {
  const wrapRef = useRef(null);
  const trailRef = useRef(null);
  useCursorInteract(enabled, wrapRef);

  useEffect(() => {
    const wrap = wrapRef.current;
    const trail = trailRef.current;
    if (!wrap || !enabled) return undefined;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    const fine = window.matchMedia("(pointer: fine)").matches;
    if (reduced || !fine) {
      wrap.style.display = "none";
      if (trail) trail.style.display = "none";
      return undefined;
    }

    let x = window.innerWidth / 2;
    let y = window.innerHeight / 2;
    let cx = x;
    let cy = y;
    let tx = x;
    let ty = y;
    let raf = 0;
    let running = false;

    const tick = () => {
      cx += (x - cx) * 0.18;
      cy += (y - cy) * 0.18;
      tx += (x - tx) * 0.08;
      ty += (y - ty) * 0.08;
      wrap.style.transform = `translate3d(${cx}px, ${cy}px, 0)`;
      if (trail) {
        trail.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
      }

      const settled =
        Math.abs(x - cx) < 0.35 &&
        Math.abs(y - cy) < 0.35 &&
        Math.abs(x - tx) < 0.35 &&
        Math.abs(y - ty) < 0.35;

      if (!settled) {
        raf = requestAnimationFrame(tick);
      } else {
        running = false;
      }
    };

    const start = () => {
      if (!running) {
        running = true;
        raf = requestAnimationFrame(tick);
      }
    };

    let lastSpeedTs = 0;
    const onMove = e => {
      const now = performance.now();
      const dx = e.clientX - x;
      const dy = e.clientY - y;
      const speed = Math.min(1, Math.hypot(dx, dy) / 40);
      x = e.clientX;
      y = e.clientY;
      wrap.classList.add("is-on");
      if (trail) trail.classList.add("is-on");
      // stretch the trail based on velocity, throttled
      if (now - lastSpeedTs > 40 && trail) {
        lastSpeedTs = now;
        trail.style.setProperty("--speed", speed.toFixed(2));
      }
      start();
    };

    const onLeave = () => {
      wrap.classList.remove("is-on");
      if (trail) trail.classList.remove("is-on");
    };

    const onDown = e => {
      const ripple = document.createElement("span");
      ripple.className = "cursor-ripple";
      ripple.style.left = `${e.clientX}px`;
      ripple.style.top = `${e.clientY}px`;
      document.body.appendChild(ripple);
      wrap.classList.add("is-pressed");
      const cleanup = () => ripple.remove();
      ripple.addEventListener("animationend", cleanup, {once: true});
      setTimeout(cleanup, 800);
    };

    const onUp = () => wrap.classList.remove("is-pressed");

    window.addEventListener("mousemove", onMove, {passive: true});
    window.addEventListener("mousedown", onDown, {passive: true});
    window.addEventListener("mouseup", onUp, {passive: true});
    document.documentElement.addEventListener("mouseleave", onLeave);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      document.documentElement.removeEventListener("mouseleave", onLeave);
    };
  }, [enabled]);

  return (
    <>
      <div ref={trailRef} className="cursor-trail" aria-hidden="true">
        <span className="cursor-trail-dot cursor-trail-dot-1" />
        <span className="cursor-trail-dot cursor-trail-dot-2" />
      </div>
      <div ref={wrapRef} className="cursor-glow" aria-hidden="true">
        <span className="cursor-glow-core" />
        <span className="cursor-glow-ring" />
        <span className="cursor-glow-dot" />
      </div>
    </>
  );
}
